import { QuestionnaireResponseItem } from "@medplum/fhirtypes";

import { getEncounterDiagnosesItems } from "@/encounters/utils/questionnaire-response";
import { EncounterAmendmentFormType } from "@/encounters/zod-schemas/encounter-answers";

/**
 * Builds encounter-level QuestionnaireResponse items for labor amend Sign & Lock.
 * Uses flat amendment linkIds to match existing labor encounter QuestionnaireResponse shape.
 */
export function buildLaborAmendEncounterQuestionnaireItems(
  parsedValues: EncounterAmendmentFormType,
): QuestionnaireResponseItem[] {
  return [
    ...(parsedValues.encounterNote
      ? [
          {
            linkId: "encounterNote",
            text: "Encounter Note",
            answer: [{ valueString: parsedValues.encounterNote }],
          },
        ]
      : []),
    ...(parsedValues.encounterClientSummary
      ? [
          {
            linkId: "encounterClientSummary",
            text: "Encounter Client Summary",
            answer: [{ valueString: parsedValues.encounterClientSummary }],
          },
        ]
      : []),
    ...(parsedValues.encounterAmendmentInfo?.encounterAmendmentInfoReason
      ? [
          {
            linkId: "encounterAmendmentInfoReason",
            answer: [
              typeof parsedValues.encounterAmendmentInfo.encounterAmendmentInfoReason === "string"
                ? {
                    valueString: parsedValues.encounterAmendmentInfo.encounterAmendmentInfoReason,
                  }
                : {
                    valueCoding: parsedValues.encounterAmendmentInfo.encounterAmendmentInfoReason,
                  },
            ],
          },
        ]
      : []),
    ...(parsedValues.encounterAmendmentInfo?.encounterAmendmentInfoSpecify
      ? [
          {
            linkId: "encounterAmendmentInfoSpecify",
            answer: [
              {
                valueString: parsedValues.encounterAmendmentInfo.encounterAmendmentInfoSpecify,
              },
            ],
          },
        ]
      : []),
    ...(parsedValues.encounterAmendmentInfo?.encounterAmendmentInfoNotes
      ? [
          {
            linkId: "encounterAmendmentInfoNotes",
            answer: [
              { valueString: parsedValues.encounterAmendmentInfo.encounterAmendmentInfoNotes },
            ],
          },
        ]
      : []),
    ...(parsedValues.encounterAmendmentInfo?.encounterAmendmentInfoNotifyClient
      ? [
          {
            linkId: "encounterAmendmentInfoNotifyClient",
            answer: [
              {
                valueBoolean:
                  parsedValues.encounterAmendmentInfo.encounterAmendmentInfoNotifyClient,
              },
            ],
          },
        ]
      : []),
    ...(parsedValues.encounterClientDiagnoses
      ? getEncounterDiagnosesItems(parsedValues.encounterClientDiagnoses)
      : []),
  ];
}

/**
 * Replaces items with matching linkId or appends new items onto the existing encounter response.
 */
export function mergeQuestionnaireResponseItemsByLinkId(
  existingItems: QuestionnaireResponseItem[],
  amendmentItems: QuestionnaireResponseItem[],
): QuestionnaireResponseItem[] {
  const updatedItems = [...existingItems];

  for (const amendmentItem of amendmentItems) {
    const existingIndex = updatedItems.findIndex((item) => item.linkId === amendmentItem.linkId);
    if (existingIndex >= 0) {
      updatedItems[existingIndex] = amendmentItem;
    } else {
      updatedItems.push(amendmentItem);
    }
  }

  return updatedItems;
}
